import { IDeployment } from 'components/deployment/Deployment';
import { useContext, useEffect, useState } from 'react';

import { RestService } from 'services/restService';
import { DeploymentResponse } from 'services/startDeploymentService';
import { optionMapper, UIOption, organizationToOptionMapper } from 'utils/mappers';
import { ApiError } from 'services/restService';
import TranslationsContext from 'translations/TranslationsContext';
import translationKeys from 'translationKeys';

interface ApiBaseHook {
	isLoading: boolean;
	error: ApiError;
}

const apiInitialState: ApiBaseHook = {
	isLoading: true,
	error: null
};

interface OrgsAndSubsHook extends ApiBaseHook {
	orgOptions: UIOption[];
	subOptions: UIOption[];
}

export const useOrgsAndSubsOptions = (): [OrgsAndSubsHook, (newState: OrgsAndSubsHook) => void] => {
	const [state, setState] = useState<OrgsAndSubsHook>({
		...apiInitialState,
		orgOptions: [],
		subOptions: []
	});

	useEffect(() => {
		Promise.all([
			RestService.get({ endPoint: '/api/Dynamics/Organizations' }),
			RestService.get({ endPoint: '/api/Azure/Subscriptions' }
			)]).then(([orgOptions, subOptions]) => {
			setState({
				error: null,
				isLoading: false,
				orgOptions: organizationToOptionMapper(orgOptions),
				subOptions: optionMapper(subOptions)
			});
		}).catch((error: ApiError) => {
			setState({
				...state,
				error,
				isLoading: false
			});
		});
	}, []);

	return [state, setState];
};

interface ResourceGroupsHook extends ApiBaseHook {
	options: UIOption[]
}

export const useResourceGroupOptions = (): [ResourceGroupsHook, (id: string) => void] => {
	const [subscriptionId, setSubscriptionId] = useState(null);
	const [state, setState] = useState<ResourceGroupsHook>({
		...apiInitialState,
		options: []
	});

	useEffect(() => {
		if (subscriptionId) {
			RestService.get({ endPoint: `/api/Azure/Subscriptions/${subscriptionId}/ResourceGroups` })
				.then((options) => {
					setState({
						error: null,
						isLoading: false,
						options: optionMapper(options)
					});
				}).catch((error: ApiError) => {
					setState({
						...state,
						error,
						isLoading: false
					});
				});
		}
	}, [subscriptionId]);

	return [state, setSubscriptionId];
};

interface DeploymentHook extends ApiBaseHook {
	log: string[],
	 url: string
}

export const useDeployment = ({ url, organizationId, resourceGroupId }: IDeployment) => {
	const [state, setState] = useState<DeploymentHook>({
		...apiInitialState,
		log: [],
		url
	});

	const callApi = async () => {
		RestService.post({ endPoint: `/api${state.url}`, data: { dataverseOrgId: organizationId, azureResourceGroup: resourceGroupId } })
			.then((data: DeploymentResponse) => {
				setState({
					...state,
					isLoading: !!data.nextUrl,
					url: data.nextUrl
				});
			})
			.catch((error: ApiError) => {
				setState({
					...state,
					error,
					isLoading: false
				});
			});
	};

	useEffect(() => {
		if (state.url) callApi();
	}, [state.url]);

	return [state];
};

export interface EnvironmentCheckResponse {
	error: any,
	isSuccess: boolean;
	result: EnvironmentCheckResponseResult;
}

export interface EnvironmentCheckResponseResult {
	solutionVersion: EnvironmentCheckItem;
	azureVersion: EnvironmentCheckItem;
	installerSteps: EnvironmentCheckItem;
}

export interface ProblemDescription {
	type: string;
	detail: string;
}

export interface EnvironmentCheckItem {
	status: string;
	statusReason?: string;
	errorCode?: number;
	problem?: ProblemDescription;
	children?: {
		[key: string]: EnvironmentCheckItem;
	}
}

interface EnvironmentCheckHook extends ApiBaseHook {
	environmentCheckResult: EnvironmentCheckResponse
}

export const useEnvironmentCheckInfo = ({ url, organizationId, resourceGroupId }: IDeployment) => {
	const [state, setState] = useState<EnvironmentCheckHook>({
		...apiInitialState,
		environmentCheckResult: null
	});

	const callApi = async () => {
		RestService.post({ endPoint: `/api${url}`, data: { dataverseOrgId: organizationId, azureResourceGroup: resourceGroupId } })
			.then((data: EnvironmentCheckResponse) => {
				setState({
					...state,
					environmentCheckResult: data,
					isLoading: false,
				});
			})
			.catch((error: ApiError) => {
				setState({
					...state,
					error,
					isLoading: false
				});
			});
	};

	useEffect(() => {
		if (url) callApi();
	}, [url]);

	return [state];
};

interface EnvironmentCheckFileHook extends ApiBaseHook {
	url: string;
}

const resultFileinitialState : EnvironmentCheckFileHook = {
	...apiInitialState,
	error: null,
	url: null
};

export const useResultFile = (environmentCheckResult: EnvironmentCheckResponseResult, resourceGroupId: string, organizationId: string): [EnvironmentCheckFileHook] => {
	const { t } = useContext(TranslationsContext)!;
	const [state, setState] = useState<EnvironmentCheckFileHook>(resultFileinitialState);

	const callApi = async () => {
		RestService.post({ endPoint: 'api/EnvironmentCheck/getfile', data: {results: environmentCheckResult, resourceGroupId: resourceGroupId, dataverseOrgId: organizationId} }, true)
			.then((data: any) => {
				let url: string;

				try {
					const blob = new Blob([data], { type: 'application/octet-stream' });
					url = window.URL.createObjectURL(blob);
				} catch (e: any) {
					setState({
						...state,
						error: {
							detail: t(translationKeys.Api.UseResultFile.CallApi.Error.Detail),
							status: 500,
							title: t(translationKeys.Api.UseResultFile.CallApi.Error.Title),
							traceId: e.traceId,
							type: 'error'
						},
						isLoading: false,
					});

					return;
				}

				setState({
					...state,
					url,
					isLoading: false,
				});
			})
			.catch((error: ApiError) => {
				setState({
					...state,
					error,
					isLoading: false
				});
			});
	};

	useEffect(() => {
		if (environmentCheckResult) callApi();
		else setState(resultFileinitialState);

	}, [environmentCheckResult]);

	return [state];
};
